import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { STRING_CONSTANTS } from '@shared-constants';
import { Invoice } from '@shared-models';

import { NoteArticleType } from '../enums/note-article-type.enum';
import { NoteItemForm } from '../models/note-item-form.model';

@Injectable({ providedIn: 'any' })
export class NoteFormCalculationService {
  recalculateRowTotals(
    group: FormGroup<NoteItemForm>,
    index: number,
    priceMode: 'absolute' | 'percentage',
    articleType: NoteArticleType,
    invoice: Invoice | undefined,
    sales: Invoice['venta'],
    taxIncluded: boolean,
  ): void {
    const qty = Number(group.controls.quantity.value) || 0;
    const price = Number(group.controls.price.value) || 0;

    // Convert percentage → absolute price
    let effectivePrice = price;
    if (priceMode === 'percentage') {
      const originalPrice =
        articleType === NoteArticleType.BY_TOTAL
          ? (invoice?.venta?.[0]?.total ?? 0)
          : (sales[index]?.precio ?? 0);
      effectivePrice = (originalPrice * price) / 100;
    }

    const taxRate = Number(group.controls.article.value?.tax?.[0]?.value ?? 0);
    const discountFactor = this.lineDiscountFactor(articleType, sales, index);
    const subtotal = qty * effectivePrice * discountFactor;
    const totalWithTax = taxIncluded ? subtotal : subtotal * (1 + taxRate);

    if (group.controls.subtotal.value !== subtotal) {
      group.controls.subtotal.setValue(subtotal, { emitEvent: false });
    }
    if (group.controls.totalWithTax.value !== totalWithTax) {
      group.controls.totalWithTax.setValue(totalWithTax, { emitEvent: false });
    }
  }

  /**
   * Percentage discount carried by the invoice line this note row credits, as a
   * multiplier. A note must mirror the discount of the line it adjusts, or it
   * exceeds the invoice it references — an annulment of a discounted invoice
   * would credit the undiscounted amount.
   *
   * BY_TOTAL prices off `venta.total`, which the invoice already stored net of
   * the discount, so applying it again would discount twice.
   */
  lineDiscountFactor(
    articleType: NoteArticleType,
    sales: Invoice['venta'],
    index: number,
  ): number {
    if (articleType === NoteArticleType.BY_TOTAL) return 1;
    const percentage = Number(sales[index]?.descuento) || 0;
    // Above 100 the factor would go negative and flip the line's sign, so a corrupt
    // value is ignored rather than applied. Exactly 100 stays valid — a fully
    // discounted line is a real case and lands on 0.
    if (percentage <= 0 || percentage > 100) return 1;
    return 1 - percentage / 100;
  }

  calculateFormArrayTotals(
    controls: FormGroup<NoteItemForm>[],
    taxIncluded: boolean,
  ): { subtotalSum: number; totalWithTaxSum: number; totalTaxSum: number } {
    return controls.reduce(
      (acc, group) => {
        const subtotal = Number(group.controls.subtotal.value) || 0;
        const totalWithTax = Number(group.controls.totalWithTax.value) || 0;
        const taxRate = Number(
          group.controls.article.value?.tax?.[0]?.value ?? 0,
        );

        acc.subtotalSum += subtotal;
        acc.totalWithTaxSum += totalWithTax;
        acc.totalTaxSum += taxIncluded
          ? subtotal - subtotal / (1 + taxRate)
          : subtotal * taxRate;

        return acc;
      },
      { subtotalSum: 0, totalWithTaxSum: 0, totalTaxSum: 0 },
    );
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: STRING_CONSTANTS.DEFAULT_CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}
